import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Gamepad2, Trophy, Star, Gift, RotateCcw, Play, Pause, Clock, Zap, Target, Info } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { api } from '../services/api';
import { toast } from 'sonner';
// 🎮 MEGA GAMES - Nouveaux jeux ultra améliorés avec sons et animations
import { 
  MegaSlotMachine, 
  MegaFortuneWheel,
  MegaCosmicDice, 
  MegaMysteryChest, 
  MegaSpinningTarget, 
  MegaDiamondHunt, 
  MegaCardPick,
  GameSounds 
} from './games';
import { WinnersShowcase } from './WinnersShowcase';
import { useTheme } from './ThemeToggle';

// ============================================
// 🎰 CONFIGURATION DES PROBABILITÉS
// Budget: 150€/semaine max, 1500 clients réguliers
// RECALCULÉ : ~10 pts moyenne par partie = 105€/semaine ✓
// ============================================

// Probabilités calculées pour respecter le budget
const PRIZE_CONFIG = {
  // Gros lots (ultra rares)
  stamp: { probability: 0.001, label: '🏆 TAMPON!', value: 'stamp', points: 0 },       // 0.1%
  discount10: { probability: 0.001, label: '🎁 -10%', value: 'discount10', points: 0 }, // 0.1%
  discount5: { probability: 0.003, label: '🎁 -5%', value: 'discount5', points: 0 },    // 0.3%
  
  // Replay (pas de coût)
  replay: { probability: 0.03, label: '🍀 Rejoue!', value: 'replay', points: 0 },       // 3%
  
  // Points (gains modérés)
  points50: { probability: 0.005, label: '⭐ 50 pts', value: '50', points: 50 },        // 0.5% = rare
  points25: { probability: 0.02, label: '⭐ 25 pts', value: '25', points: 25 },         // 2%
  points10: { probability: 0.10, label: '⭐ 10 pts', value: '10', points: 10 },         // 10%
  
  // Consolation (toujours quelque chose mais peu)
  points5: { probability: 0.84, label: '✨ 5 pts', value: '5', points: 5 },             // 84%
};

// Fonction pour déterminer le gain
const determineWinning = () => {
  const random = Math.random();
  let cumulative = 0;
  
  for (const [key, config] of Object.entries(PRIZE_CONFIG)) {
    cumulative += config.probability;
    if (random <= cumulative) {
      return { key, ...config };
    }
  }
  
  // Fallback - points de consolation
  return { key: 'points25', ...PRIZE_CONFIG.points25 };
};

// ============================================
// 🎡 ROUE DE LA FORTUNE
// ============================================
const WHEEL_SEGMENTS = [
  { label: '🏆 TAMPON', color: '#FFD700', prizeKey: 'stamp' },
  { label: '✨ 5 pts', color: '#6366F1', prizeKey: 'points5' },
  { label: '🎁 -5%', color: '#EF4444', prizeKey: 'discount5' },
  { label: '✨ 5 pts', color: '#8B5CF6', prizeKey: 'points5' },
  { label: '🍀 Rejoue', color: '#22C55E', prizeKey: 'replay' },
  { label: '⭐ 10 pts', color: '#3B82F6', prizeKey: 'points10' },
  { label: '✨ 5 pts', color: '#EC4899', prizeKey: 'points5' },
  { label: '⭐ 25 pts', color: '#F59E0B', prizeKey: 'points25' },
];

export function FortuneWheel({ customerId, onWin, gameStatus, isTraining, onGameEnd }) {
  const [isSpinning, setIsSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [result, setResult] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const { isDarkMode } = useTheme();

  const spinWheel = async () => {
    if (isSpinning) return;
    
    setIsSpinning(true);
    setShowResult(false);
    setResult(null);

    // Déterminer le résultat
    const winning = determineWinning();
    
    // Trouver le segment correspondant
    const segmentIndex = WHEEL_SEGMENTS.findIndex(s => s.prizeKey === winning.key);
    const segmentAngle = 360 / WHEEL_SEGMENTS.length;
    const targetAngle = 360 - (segmentIndex * segmentAngle) - (segmentAngle / 2);
    const spins = 5 + Math.floor(Math.random() * 3);
    const finalRotation = rotation + (spins * 360) + targetAngle;

    setRotation(finalRotation);

    // Attendre la fin de l'animation
    setTimeout(async () => {
      setIsSpinning(false);
      setResult(winning);
      setShowResult(true);
      
      // Enregistrer la partie
      try {
        const prizeType = winning.points > 0 ? 'points' : 
                         winning.key === 'replay' ? 'replay' :
                         winning.key === 'stamp' ? 'stamp' :
                         winning.key.startsWith('discount') ? 'discount' : 'nothing';
        
        const prizeValue = winning.points > 0 ? winning.value : 
                          winning.key.startsWith('discount') ? winning.key : winning.value;
        
        const response = await api.recordGamePlay(
          customerId,
          'wheel',
          prizeType,
          prizeValue,
          null,
          isTraining
        );
        
        if (response.message) {
          toast.success(response.message);
        }
        
        if (onWin) onWin(winning);
        if (onGameEnd) onGameEnd();
      } catch (error) {
        console.error('Erreur enregistrement:', error);
      }
    }, 5000);
  };

  return (
    <div className="flex flex-col items-center">
      {/* Indicateur mode */}
      {isTraining && (
        <div className="mb-4 px-4 py-2 rounded-full bg-blue-500/20 border border-blue-500/50">
          <span className="text-blue-400 text-sm font-bold">🎯 MODE ENTRAÎNEMENT - Pas de gain réel</span>
        </div>
      )}
      
      {/* Roue */}
      <div className="relative w-64 h-64 sm:w-72 sm:h-72 mb-6">
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-2 z-10">
          <div className="w-0 h-0 border-l-[15px] border-r-[15px] border-t-[25px] border-l-transparent border-r-transparent border-t-yellow-400 drop-shadow-lg" />
        </div>
        
        <motion.div
          className="w-full h-full rounded-full border-4 border-yellow-400 overflow-hidden shadow-2xl"
          style={{
            background: `conic-gradient(${WHEEL_SEGMENTS.map((s, i) => 
              `${s.color} ${i * (100/WHEEL_SEGMENTS.length)}% ${(i+1) * (100/WHEEL_SEGMENTS.length)}%`
            ).join(', ')})`,
          }}
          animate={{ rotate: rotation }}
          transition={{ duration: 5, ease: [0.17, 0.67, 0.12, 0.99] }}
        >
          {WHEEL_SEGMENTS.map((segment, index) => {
            const angle = (index * 360 / WHEEL_SEGMENTS.length) + (180 / WHEEL_SEGMENTS.length);
            return (
              <div
                key={index}
                className="absolute w-full h-full flex items-center justify-center"
                style={{ transform: `rotate(${angle}deg)` }}
              >
                <span 
                  className="text-white text-[10px] font-bold drop-shadow-lg"
                  style={{ 
                    transform: 'translateY(-90px) rotate(90deg)',
                    textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
                  }}
                >
                  {segment.label}
                </span>
              </div>
            );
          })}
        </motion.div>
        
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 border-4 border-white shadow-xl flex items-center justify-center">
          <span className="text-2xl">🎰</span>
        </div>
      </div>

      <Button
        onClick={spinWheel}
        disabled={isSpinning}
        className="px-8 py-4 text-lg font-bold rounded-full"
        style={{
          background: isSpinning 
            ? 'linear-gradient(135deg, #666 0%, #444 100%)' 
            : 'linear-gradient(135deg, #FFD700 0%, #FF8C00 100%)',
          color: '#000'
        }}
      >
        {isSpinning ? '🎰 Ça tourne...' : '🎯 TOURNER !'}
      </Button>

      <AnimatePresence>
        {showResult && result && (
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            className={`mt-4 p-4 rounded-xl text-center ${
              result.points > 0 || result.key !== 'points25'
                ? 'bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500'
                : 'bg-gray-700/50'
            }`}
          >
            <span className="text-xl font-bold text-white">{result.label}</span>
            {isTraining && <p className="text-xs text-gray-400 mt-1">(Mode entraînement)</p>}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ============================================
// 🎰 MACHINE À SOUS - SIMPLIFIÉE
// ============================================
const SLOT_SYMBOLS = ['🍋', '🍊', '🍇', '🍒', '⭐', '💎', '7️⃣', '🏆'];

export function SlotMachine({ customerId, onWin, gameStatus, isTraining, onGameEnd }) {
  const [reels, setReels] = useState([0, 0, 0]);
  const [isSpinning, setIsSpinning] = useState(false);
  const [result, setResult] = useState(null);
  const { isDarkMode } = useTheme();

  const spin = async () => {
    if (isSpinning) return;
    
    setIsSpinning(true);
    setResult(null);

    // Animation des rouleaux
    const animateReel = (index, delay) => {
      return new Promise(resolve => {
        setTimeout(() => {
          const finalSymbol = Math.floor(Math.random() * SLOT_SYMBOLS.length);
          setReels(prev => {
            const newReels = [...prev];
            newReels[index] = finalSymbol;
            return newReels;
          });
          resolve(finalSymbol);
        }, delay);
      });
    };

    // Animation séquentielle
    await animateReel(0, 1000);
    await animateReel(1, 500);
    const finalReels = await animateReel(2, 500);

    // Déterminer le gain basé sur les probabilités (pas sur les symboles visuels)
    const winning = determineWinning();
    
    setIsSpinning(false);
    setResult(winning);

    // Enregistrer la partie
    try {
      const prizeType = winning.points > 0 ? 'points' : 
                       winning.key === 'replay' ? 'replay' :
                       winning.key === 'stamp' ? 'stamp' :
                       winning.key.startsWith('discount') ? 'discount' : 'nothing';
      
      const prizeValue = winning.points > 0 ? winning.value : 
                        winning.key.startsWith('discount') ? winning.key : winning.value;
      
      const response = await api.recordGamePlay(
        customerId,
        'slots',
        prizeType,
        prizeValue,
        null,
        isTraining
      );
      
      if (response.message) {
        toast.success(response.message);
      }
      
      if (onWin) onWin(winning);
      if (onGameEnd) onGameEnd();
    } catch (error) {
      console.error('Erreur enregistrement:', error);
    }
  };

  return (
    <div className="flex flex-col items-center p-6">
      {isTraining && (
        <div className="mb-4 px-4 py-2 rounded-full bg-blue-500/20 border border-blue-500/50">
          <span className="text-blue-400 text-sm font-bold">🎯 MODE ENTRAÎNEMENT</span>
        </div>
      )}
      
      <h3 className="text-2xl font-bold text-yellow-400 mb-4">🎰 JACKPOT LPE 🎰</h3>
      
      <div className="flex gap-2 mb-6">
        {reels.map((symbolIndex, i) => (
          <motion.div
            key={i}
            className="w-20 h-24 rounded-xl bg-gradient-to-b from-gray-800 to-gray-900 border-2 border-yellow-500 flex items-center justify-center text-4xl"
            animate={isSpinning ? { y: [0, -5, 0] } : {}}
            transition={isSpinning ? { duration: 0.1, repeat: Infinity } : {}}
          >
            {SLOT_SYMBOLS[symbolIndex]}
          </motion.div>
        ))}
      </div>

      <Button
        onClick={spin}
        disabled={isSpinning}
        className="px-8 py-4 text-lg font-bold rounded-full"
        style={{
          background: isSpinning 
            ? 'linear-gradient(135deg, #666 0%, #444 100%)' 
            : 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
        }}
      >
        {isSpinning ? '🎰 En cours...' : '🎲 LANCER !'}
      </Button>

      {result && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 p-4 rounded-xl bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500"
        >
          <span className="text-xl font-bold text-white">{result.label}</span>
        </motion.div>
      )}
    </div>
  );
}

// ============================================
// 🐍 SNAKE LPE - Jeu d'adresse (illimité)
// ============================================
export function SnakeGame({ customerId, onScore }) {
  const canvasRef = useRef(null);
  const [gameState, setGameState] = useState('idle');
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(() => {
    const saved = localStorage.getItem('lpe_snake_highscore');
    return saved ? parseInt(saved) : 0;
  });
  const gameRef = useRef({
    snake: [{ x: 10, y: 10 }],
    direction: { x: 1, y: 0 },
    food: { x: 15, y: 15 },
    speed: 100,
  });

  const GRID_SIZE = 20;
  const CELL_SIZE = 15;

  const startGame = () => {
    gameRef.current = {
      snake: [{ x: 10, y: 10 }],
      direction: { x: 1, y: 0 },
      food: { x: Math.floor(Math.random() * GRID_SIZE), y: Math.floor(Math.random() * GRID_SIZE) },
      speed: 100,
    };
    setScore(0);
    setGameState('playing');
  };

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const { snake, food } = gameRef.current;

    ctx.fillStyle = '#0a0a1a';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Food
    ctx.fillStyle = '#FF4444';
    ctx.beginPath();
    ctx.arc(
      food.x * CELL_SIZE + CELL_SIZE / 2,
      food.y * CELL_SIZE + CELL_SIZE / 2,
      CELL_SIZE / 2 - 2, 0, 2 * Math.PI
    );
    ctx.fill();

    // Snake
    snake.forEach((segment, index) => {
      ctx.fillStyle = index === 0 ? '#00FF00' : '#00DD00';
      ctx.fillRect(segment.x * CELL_SIZE + 1, segment.y * CELL_SIZE + 1, CELL_SIZE - 2, CELL_SIZE - 2);
    });
  }, []);

  const gameLoop = useCallback(() => {
    if (gameState !== 'playing') return;

    const { snake, direction, food } = gameRef.current;
    
    const newHead = {
      x: (snake[0].x + direction.x + GRID_SIZE) % GRID_SIZE,
      y: (snake[0].y + direction.y + GRID_SIZE) % GRID_SIZE,
    };

    if (snake.some(segment => segment.x === newHead.x && segment.y === newHead.y)) {
      setGameState('gameover');
      if (score > highScore) {
        setHighScore(score);
        localStorage.setItem('lpe_snake_highscore', score.toString());
        toast.success(`🏆 Nouveau record : ${score} points !`);
      }
      // Convertir le score en points (1 point snake = 1 point fidélité)
      if (score > 0) {
        api.recordGamePlay(customerId, 'snake', 'points', score.toString(), score, false);
      }
      return;
    }

    const newSnake = [newHead, ...snake];

    if (newHead.x === food.x && newHead.y === food.y) {
      gameRef.current.food = {
        x: Math.floor(Math.random() * GRID_SIZE),
        y: Math.floor(Math.random() * GRID_SIZE)
      };
      setScore(s => s + 10);
      gameRef.current.speed = Math.max(50, gameRef.current.speed - 2);
    } else {
      newSnake.pop();
    }

    gameRef.current.snake = newSnake;
    draw();
  }, [gameState, score, highScore, draw, customerId]);

  useEffect(() => {
    if (gameState !== 'playing') return;
    const interval = setInterval(gameLoop, gameRef.current.speed);
    return () => clearInterval(interval);
  }, [gameState, gameLoop]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (gameState !== 'playing') return;
      const { direction } = gameRef.current;
      
      const keyMap = {
        'ArrowUp': { x: 0, y: -1, block: { y: 1 } },
        'ArrowDown': { x: 0, y: 1, block: { y: -1 } },
        'ArrowLeft': { x: -1, y: 0, block: { x: 1 } },
        'ArrowRight': { x: 1, y: 0, block: { x: -1 } },
      };
      
      const newDir = keyMap[e.key];
      if (newDir && direction.x !== newDir.block?.x && direction.y !== newDir.block?.y) {
        gameRef.current.direction = { x: newDir.x, y: newDir.y };
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [gameState]);

  useEffect(() => {
    if (gameState === 'idle') draw();
  }, [gameState, draw]);

  return (
    <div className="flex flex-col items-center">
      <div className="flex gap-6 mb-4">
        <div className="text-center">
          <div className="text-xs text-gray-400">Score</div>
          <div className="text-2xl font-bold text-green-400">{score}</div>
        </div>
        <div className="text-center">
          <div className="text-xs text-gray-400">Record</div>
          <div className="text-2xl font-bold text-yellow-400">{highScore}</div>
        </div>
      </div>

      <div className="relative">
        <canvas
          ref={canvasRef}
          width={GRID_SIZE * CELL_SIZE}
          height={GRID_SIZE * CELL_SIZE}
          className="rounded-xl border-2 border-green-500"
        />
        
        {gameState !== 'playing' && (
          <div className="absolute inset-0 bg-black/70 rounded-xl flex flex-col items-center justify-center">
            {gameState === 'gameover' && (
              <div className="text-2xl font-bold text-red-400 mb-4">GAME OVER</div>
            )}
            <Button onClick={startGame} className="bg-green-500 hover:bg-green-600">
              <Play className="w-5 h-5 mr-2" />
              {gameState === 'gameover' ? 'Rejouer' : 'Jouer'}
            </Button>
          </div>
        )}
      </div>

      <p className="mt-4 text-gray-500 text-xs">Flèches ⬆️⬇️⬅️➡️ pour diriger • Score = Points fidélité</p>
    </div>
  );
}

// ============================================
// 🎮 COMPOSANT PRINCIPAL GAMES HUB
// ============================================
export default function GamesHub({ customerId }) {
  const [activeGame, setActiveGame] = useState(null);
  const [gameStatus, setGameStatus] = useState(null);
  const [isTraining, setIsTraining] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const { isDarkMode } = useTheme();

  // 8 JEUX DISPONIBLES
  const games = [
    { id: 'wheel', name: 'Roue Fortune', icon: '🎡', color: 'from-yellow-500 to-orange-500' },
    { id: 'slots', name: 'Jackpot', icon: '🎰', color: 'from-purple-500 to-pink-500' },
    { id: 'dice', name: 'Dés Cosmiques', icon: '🎲', color: 'from-indigo-500 to-purple-500' },
    { id: 'chest', name: 'Coffre Mystère', icon: '📦', color: 'from-amber-500 to-yellow-500' },
    { id: 'target', name: 'Cible Tournante', icon: '🎯', color: 'from-red-500 to-orange-500' },
    { id: 'diamond', name: 'Chasse Diamants', icon: '💎', color: 'from-cyan-500 to-blue-500' },
    { id: 'cards', name: 'Le Bon Choix', icon: '🃏', color: 'from-green-500 to-emerald-500' },
    { id: 'snake', name: 'Snake LPE', icon: '🐍', color: 'from-lime-500 to-green-500', unlimited: true },
  ];

  // Charger le statut des jeux
  useEffect(() => {
    const loadStatus = async () => {
      if (!customerId) return;
      try {
        const status = await api.getGameStatus(customerId);
        setGameStatus(status);
      } catch (error) {
        console.error('Erreur chargement statut:', error);
      }
    };
    loadStatus();
  }, [customerId]);

  const refreshStatus = async () => {
    if (!customerId) return;
    try {
      const status = await api.getGameStatus(customerId);
      setGameStatus(status);
    } catch (error) {
      console.error('Erreur refresh statut:', error);
    }
  };

  const handleGameSelect = (game) => {
    if (game.unlimited) {
      // Snake est illimité
      setIsTraining(false);
      setActiveGame(game.id);
    } else if (gameStatus?.canPlayOfficial) {
      // Partie officielle disponible
      setIsTraining(false);
      setActiveGame(game.id);
    } else if (gameStatus?.canPlayTraining) {
      // Seulement entraînement disponible
      setIsTraining(true);
      setActiveGame(game.id);
    } else {
      toast.error("Tu as épuisé tes parties du jour ! Reviens demain 🎮");
    }
  };

  const handleWin = (prize) => {
    if (prize?.label) {
      // Le toast est déjà affiché par le jeu
    }
  };

  const handleGameEnd = () => {
    refreshStatus();
  };

  return (
    <div className="w-full max-w-full overflow-hidden box-border">
      {/* Showcase des gagnants */}
      <div className="mb-4">
        <WinnersShowcase />
      </div>
      
      {/* Barre de statut */}
      {gameStatus && (
        <div className={`mb-4 p-3 rounded-xl ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-100'}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            {/* Points */}
            <div className="flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-500" />
              <div>
                <span className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {gameStatus.gamePoints || 0} pts
                </span>
                <span className={`text-xs ml-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  ({gameStatus.pointsForNextStamp || 1000} → 🏆)
                </span>
              </div>
            </div>
            
            {/* Parties restantes */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                <Zap className="w-4 h-4 text-green-500" />
                <span className={`text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {gameStatus.officialPlaysRemaining || 0} officielle
                </span>
              </div>
              <div className="flex items-center gap-1">
                <Target className="w-4 h-4 text-blue-500" />
                <span className={`text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {gameStatus.trainingPlaysRemaining || 0} entraîn.
                </span>
              </div>
            </div>
            
            {/* Info */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowInfo(true)}
              className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}
            >
              <Info className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
      
      {/* Sélection des jeux */}
      {!activeGame && (
        <div className="w-full">
          <div className="text-center mb-4">
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              🎮 8 jeux disponibles ! 1 partie officielle + 3 entraînements par jour
            </p>
          </div>
          <div className="grid grid-cols-4 gap-2 w-full" style={{ maxWidth: '100%' }}>
            {games.map((game) => (
              <motion.button
                key={game.id}
                onClick={() => handleGameSelect(game)}
                className={`rounded-xl bg-gradient-to-br ${game.color} flex flex-col items-center justify-center shadow-lg transition-transform`}
                style={{ 
                  aspectRatio: '1/1',
                  padding: '8px',
                  minWidth: 0,
                  maxWidth: '100%'
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span className="text-2xl leading-none">{game.icon}</span>
                <span className="text-[9px] font-bold text-white text-center leading-tight mt-1 truncate w-full px-1">{game.name}</span>
                {game.unlimited && (
                  <span className="text-[7px] text-white/80 bg-black/20 px-1 rounded mt-0.5">∞</span>
                )}
              </motion.button>
            ))}
          </div>
          
          {/* Légende */}
          <div className={`mt-4 text-center text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            <p>🎯 <strong>Partie officielle</strong> = Gains réels (tampons, réductions, points)</p>
            <p>🎓 <strong>Entraînement</strong> = Pour s'amuser (points de consolation)</p>
            <p>⭐ <strong>1000 points</strong> = 1 tampon bonus sur ta carte !</p>
          </div>
        </div>
      )}

      {/* Jeu actif */}
      {activeGame && (
        <div>
          <Button
            variant="ghost"
            onClick={() => { setActiveGame(null); refreshStatus(); }}
            className={isDarkMode ? 'mb-4 text-gray-400' : 'mb-4 text-gray-600'}
          >
            ← Retour aux jeux
          </Button>
          
          {/* Indicateur mode entraînement */}
          {isTraining && activeGame !== 'snake' && (
            <div className="mb-4 px-4 py-2 rounded-full bg-blue-500/20 border border-blue-500/50 text-center">
              <span className="text-blue-400 text-sm font-bold">🎯 MODE ENTRAÎNEMENT - Points de consolation uniquement</span>
            </div>
          )}

          {activeGame === 'wheel' && (
            <MegaFortuneWheel 
              customerId={customerId} 
              onWin={handleWin} 
              gameStatus={gameStatus}
              isTraining={isTraining}
              onGameEnd={handleGameEnd}
            />
          )}
          {activeGame === 'slots' && (
            <MegaSlotMachine 
              customerId={customerId} 
              onWin={handleWin}
              gameStatus={gameStatus}
              isTraining={isTraining}
              onGameEnd={handleGameEnd}
            />
          )}
          {activeGame === 'dice' && (
            <MegaCosmicDice 
              customerId={customerId} 
              onWin={handleWin}
              isTraining={isTraining}
              onGameEnd={handleGameEnd}
            />
          )}
          {activeGame === 'chest' && (
            <MegaMysteryChest 
              customerId={customerId} 
              onWin={handleWin}
              isTraining={isTraining}
              onGameEnd={handleGameEnd}
            />
          )}
          {activeGame === 'target' && (
            <MegaSpinningTarget 
              customerId={customerId} 
              onWin={handleWin}
              isTraining={isTraining}
              onGameEnd={handleGameEnd}
            />
          )}
          {activeGame === 'diamond' && (
            <MegaDiamondHunt 
              customerId={customerId} 
              onWin={handleWin}
              isTraining={isTraining}
              onGameEnd={handleGameEnd}
            />
          )}
          {activeGame === 'cards' && (
            <MegaCardPick 
              customerId={customerId} 
              onWin={handleWin}
              isTraining={isTraining}
              onGameEnd={handleGameEnd}
            />
          )}
          {activeGame === 'snake' && (
            <SnakeGame customerId={customerId} onScore={handleWin} />
          )}
        </div>
      )}
      
      {/* Modal Info */}
      <Dialog open={showInfo} onOpenChange={setShowInfo}>
        <DialogContent className={`sm:max-w-md ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${isDarkMode ? 'text-white' : ''}`}>
              <Info className="w-5 h-5 text-blue-500" />
              Comment ça marche ?
            </DialogTitle>
          </DialogHeader>
          <div className={`space-y-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            <div>
              <h4 className={`font-bold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>🎯 Parties quotidiennes</h4>
              <p className="text-sm">• <strong>1 partie officielle</strong> par jour avec gains réels</p>
              <p className="text-sm">• <strong>3 entraînements</strong> pour s'amuser (25 pts de consolation)</p>
            </div>
            
            <div>
              <h4 className={`font-bold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>⭐ Système de points</h4>
              <p className="text-sm">Accumule des points à chaque partie !</p>
              <p className="text-sm font-bold text-yellow-500">1000 points = 1 TAMPON BONUS 🏆</p>
            </div>
            
            <div>
              <h4 className={`font-bold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>🎁 Gains possibles</h4>
              <ul className="text-sm space-y-1">
                <li>🏆 Tampon gratuit (rare)</li>
                <li>🎁 -10% / -5% de réduction</li>
                <li>🍀 Rejoue (partie bonus)</li>
                <li>⭐ 25 à 200 points</li>
              </ul>
            </div>
            
            <div>
              <h4 className={`font-bold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>🐍 Snake LPE</h4>
              <p className="text-sm">Jeu d'adresse illimité ! Ton score = tes points fidélité.</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
